import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Play, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnswerSynthesisEmpty } from '@/components/illustrations/AnswerSynthesisEmpty';
import { apiGet } from '@/api/client';
import { ApiRequestError } from '@/types/api';
import { executeAnswer } from '@/api/answers';
import { PageHeader } from '@/components/shared/PageHeader';
import { AnswerSynthesisInfoTooltip } from '@/components/shared/AnswerSynthesisInfoTooltip';
import { InlineAlert } from '@/components/shared/InlineAlert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import type { AnswerResponse, RetrievalDiagnostics } from '@/types/answer';

interface Connector {
  id: string;
  name: string;
  type: string;
}

interface Principal {
  id: string;
  display_name: string;
  email: string;
}

export function AnswerSynthesisPage() {
  const [query, setQuery] = useState('');
  const [principalId, setPrincipalId] = useState('');
  const [connectorId, setConnectorId] = useState('');
  const [topK, setTopK] = useState(15);
  const [searchMode, setSearchMode] = useState<'vector' | 'keyword' | 'hybrid'>('vector');
  const [alpha, setAlpha] = useState(0.5);
  const [result, setResult] = useState<AnswerResponse | null>(null);
  const [showRawJson, setShowRawJson] = useState(false);

  const { data: connectors, isLoading: connectorsLoading, isError: connectorsError } = useQuery({
    queryKey: ['connectors-for-answers'],
    queryFn: async () => {
      const res = await apiGet<{ data: Connector[] }>('/connectors', { per_page: 100 });
      return res.data;
    },
  });

  const { data: principals, isLoading: principalsLoading, isError: principalsError } = useQuery({
    queryKey: ['principals-for-answers'],
    queryFn: async () => {
      const res = await apiGet<{ data: Principal[] }>('/principals', { per_page: 100 });
      return res.data;
    },
  });

  const mutation = useMutation({
    mutationFn: executeAnswer,
    onSuccess: (data) => {
      setResult(data);
    },
  });

  const trimmedQuery = query.trim();
  const queryValid = trimmedQuery.length > 0 && trimmedQuery.length <= 2000;
  const clampedTopK = Math.max(1, Math.min(20, topK));
  const canSubmit = queryValid && principalId && connectorId && !mutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setShowRawJson(false);
    mutation.mutate({
      query: trimmedQuery,
      principal_id: principalId,
      connector_id: connectorId,
      top_k: clampedTopK,
      search_mode: searchMode,
      ...(searchMode === 'hybrid' ? { alpha } : {}),
    });
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        eyebrow="Observe"
        title="Answer Synthesis"
        subtitle="Ask natural language questions against policy-filtered retrieval chunks."
      />

      <div className="rounded-lg border bg-card/80 backdrop-blur-sm p-6 shadow-elevation">
        <div className="flex items-center gap-1.5 mb-4">
          <p className="text-sm font-medium text-muted-foreground">Configure your query</p>
          <AnswerSynthesisInfoTooltip />
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Query</label>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. What are the key findings from Q4 customer research?"
              className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              maxLength={2000}
            />
            {query.length > 0 && (
              <p className="text-xs text-muted-foreground text-right">{query.length}/2000</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search Mode</label>
              <Select value={searchMode} onValueChange={(v) => setSearchMode(v as 'vector' | 'keyword' | 'hybrid')}>
                <SelectTrigger>
                  <SelectValue placeholder="Select search mode..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vector">Vector (semantic)</SelectItem>
                  <SelectItem value="keyword">Keyword (BM25/FTS)</SelectItem>
                  <SelectItem value="hybrid">Hybrid (vector + keyword)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {searchMode === 'hybrid' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Alpha ({alpha.toFixed(2)})</label>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={alpha}
                  onChange={(e) => setAlpha(Number(e.target.value))}
                  className="w-full h-10"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Keyword</span>
                  <span>Vector</span>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Principal</label>
              {principalsLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : principalsError ? (
                <p className="text-sm text-destructive">Failed to load principals</p>
              ) : (principals ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No principals available</p>
              ) : (
                <Select value={principalId} onValueChange={(v) => setPrincipalId(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a principal..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(principals ?? []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.display_name} ({p.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Connector</label>
              {connectorsLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : connectorsError ? (
                <p className="text-sm text-destructive">Failed to load connectors</p>
              ) : (connectors ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No connectors available</p>
              ) : (
                <Select value={connectorId} onValueChange={(v) => setConnectorId(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a connector..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(connectors ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} ({c.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Top-K</label>
              <Input
                type="number"
                value={topK}
                onChange={(e) => setTopK(Number(e.target.value))}
                min={1}
                max={20}
              />
            </div>
          </div>

          <Button type="submit" disabled={!canSubmit}>
            <Play className="h-4 w-4 mr-2" />
            {mutation.isPending ? 'Synthesizing...' : 'Ask'}
          </Button>
          {mutation.isError && (
            <p className="text-sm text-destructive">
              {mutation.error instanceof ApiRequestError && mutation.error.status === 429
                ? 'Rate limit reached. Up to 20 answer requests per minute per org. Please wait a moment and try again.'
                : mutation.error instanceof ApiRequestError && mutation.error.code === 'LLM_CREDIT_EXHAUSTED'
                ? <>Your 100 free synthesis calls have been used. <Link to="/settings?tab=organization" className="underline font-medium">Add your OpenAI key in Settings</Link> to continue.</>
                : mutation.error instanceof ApiRequestError && mutation.error.code === 'LLM_KEY_NOT_CONFIGURED'
                ? <>Answer synthesis requires an OpenAI key. <Link to="/settings?tab=organization" className="underline font-medium">Add one in Settings → Organization.</Link></>
                : mutation.error instanceof Error ? mutation.error.message : 'Answer synthesis failed'}
            </p>
          )}
        </form>
      </div>

      {result?.cap_reached && (
        <InlineAlert variant="warning">
          You&apos;ve reached your configured query cap for this key.{' '}
          <Link to="/organization" className="underline font-medium">Rotate the key in Organization Settings</Link>{' '}
          to reset the counter.
        </InlineAlert>
      )}

      {result && <AnswerResult result={result} showRawJson={showRawJson} onToggleRawJson={() => setShowRawJson((v) => !v)} />}

      {!result && !mutation.isPending && (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <AnswerSynthesisEmpty className="h-28 w-28 mx-auto mb-2 opacity-80" />
          <h3 className="text-lg font-semibold">Enter a question above to get a grounded answer</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Select a principal and connector, then ask a question. The answer will be grounded in allowed retrieval chunks only.
          </p>
        </div>
      )}
    </div>
  );
}

const POOR_OUTCOMES = new Set(['no_access', 'insufficient_context', 'denied']);

function RetrievalDiagnosticsPanel({ diagnostics, outcome }: { diagnostics: RetrievalDiagnostics; outcome: string }) {
  const isPoor = POOR_OUTCOMES.has(outcome);

  // Zero candidates is not a policy denial: nothing was ever evaluated, so framing it as
  // "denied by policy" sends the user chasing the wrong fix.
  if (diagnostics.candidates_fetched === 0) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-status-warning/30 bg-status-warning/5 px-4 py-3 text-sm">
        <AlertTriangle className="h-4 w-4 text-status-warning shrink-0 mt-0.5" />
        <p>
          0 candidates retrieved. This usually means the connector&apos;s vectors are not
          registered as resources yet, or the query embedding dimension does not match the
          corpus. It is not a policy denial.
        </p>
      </div>
    );
  }

  if (!isPoor) {
    // Collapsed info strip for successful outcomes
    if (diagnostics.candidates_denied === 0) {
      return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
          <CheckCircle2 className="h-3.5 w-3.5 text-status-allow shrink-0" />
          <span>
            {diagnostics.candidates_fetched} candidate{diagnostics.candidates_fetched !== 1 ? 's' : ''} evaluated, all authorized.
          </span>
        </div>
      );
    }
    return (
      <div className="space-y-1 px-1">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <XCircle className="h-3.5 w-3.5 text-status-deny shrink-0" />
          <span>
            {diagnostics.candidates_fetched} candidate{diagnostics.candidates_fetched !== 1 ? 's' : ''} evaluated:{' '}
            {diagnostics.candidates_allowed} authorized,{' '}
            <span className="font-medium text-status-deny">{diagnostics.candidates_denied} denied by policy</span>.
          </span>
        </div>
        {diagnostics.active_denial_reasons.slice(0, 1).map((reason, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-status-deny">
            <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{reason}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <details className="rounded-lg border bg-card group" open>
      <summary className="flex cursor-pointer items-center gap-2 px-6 py-3 text-sm font-medium select-none list-none [&::-webkit-details-marker]:hidden hover:bg-muted/40 rounded-lg transition-colors">
        <AlertTriangle className="h-4 w-4 text-status-warning shrink-0" />
        <span>Retrieval diagnostics</span>
        <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground transition-transform group-open:rotate-90" />
      </summary>

      <div className="px-6 pb-5 pt-1 space-y-4 border-t">
        {/* outcome_detail — lead explanation */}
        {diagnostics.outcome_detail && (
          <div className="rounded-md bg-status-warning/10 border border-status-warning/20 px-4 py-3">
            <p className="text-xs font-medium text-status-warning mb-0.5">Why this outcome</p>
            <p className="text-sm">{diagnostics.outcome_detail}</p>
          </div>
        )}

        {/* Counts grid */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="rounded-md border px-3 py-2.5 text-center">
            <p className="text-xs text-muted-foreground">Fetched</p>
            <p className="font-semibold tabular mt-0.5">{diagnostics.candidates_fetched}</p>
          </div>
          <div className="rounded-md border border-status-allow/30 bg-status-allow/5 px-3 py-2.5 text-center">
            <p className="text-xs text-muted-foreground">Allowed</p>
            <p className="font-semibold tabular text-status-allow mt-0.5">{diagnostics.candidates_allowed}</p>
          </div>
          <div className="rounded-md border border-status-deny/30 bg-status-deny/5 px-3 py-2.5 text-center">
            <p className="text-xs text-muted-foreground">Denied</p>
            <p className="font-semibold tabular text-status-deny mt-0.5">{diagnostics.candidates_denied}</p>
          </div>
        </div>

        {/* Refill rounds */}
        {diagnostics.refill_rounds > 0 && (
          <p className="text-xs text-muted-foreground">
            Refill ran {diagnostics.refill_rounds} round{diagnostics.refill_rounds !== 1 ? 's' : ''} to find authorized content.
          </p>
        )}

        {/* Policies evaluated */}
        <p className="text-xs text-muted-foreground">
          {diagnostics.policies_evaluated} {diagnostics.policies_evaluated === 1 ? 'policy' : 'policies'} evaluated.
        </p>

        {/* Top denial reasons */}
        {diagnostics.active_denial_reasons.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Top denial reasons</p>
            {diagnostics.active_denial_reasons.slice(0, 3).map((reason, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-status-deny">
                <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{reason}</span>
              </div>
            ))}
          </div>
        )}

        {/* Link to simulator */}
        <p className="text-xs text-muted-foreground pt-1">
          Investigate further in the{' '}
          <Link to="/access-simulator" className="text-primary underline underline-offset-2">
            Access Simulator
          </Link>
          .
        </p>
      </div>
    </details>
  );
}

function AnswerResult({ result, showRawJson, onToggleRawJson }: { result: AnswerResponse; showRawJson: boolean; onToggleRawJson: () => void }) {
  const outcomeDisplay = getOutcomeDisplay(result.outcome, result.is_partial);

  return (
    <div className="space-y-4">
      {/* Outcome header */}
      <div className={`rounded-lg border p-6 ${outcomeDisplay.bgClassName}`}>
        <div className="flex items-center gap-3 mb-2">
          <outcomeDisplay.icon className={`h-6 w-6 ${outcomeDisplay.className}`} />
          <h3 className={`font-semibold text-lg ${outcomeDisplay.className}`}>
            {outcomeDisplay.label}
          </h3>
        </div>
        {outcomeDisplay.body && (
          <p className="text-sm text-muted-foreground">{outcomeDisplay.body}</p>
        )}
      </div>

      {/* Retrieval diagnostics */}
      {result.diagnostics != null && (
        <RetrievalDiagnosticsPanel diagnostics={result.diagnostics} outcome={result.outcome} />
      )}

      {/* Answer text */}
      {result.answer && (
        <div className="rounded-lg border bg-card p-6">
          <h3 className="font-semibold mb-3">Answer</h3>
          <p className="text-sm whitespace-pre-wrap">{result.answer}</p>
        </div>
      )}

      {/* Citations */}
      {result.citations.length > 0 && (
        <div className="rounded-lg border bg-card p-6">
          <h3 className="font-semibold mb-3">Cited {result.citations.length} source{result.citations.length !== 1 ? 's' : ''}</h3>
          <div className="space-y-3">
            {result.citations.map((c, i) => (
              <div key={i} className="rounded-md border px-4 py-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground font-mono">
                    {c.resource_id || c.vector_id}
                  </span>
                  {c.score != null && (
                    <Badge variant="secondary" className="text-xs">
                      {c.score.toFixed(3)}
                    </Badge>
                  )}
                </div>
                <p className="text-sm">{c.text_excerpt}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metrics */}
      <div className="rounded-lg border bg-card p-6">
        <h3 className="font-semibold mb-3">Diagnostics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {result.model && (
            <MetricCell label="Model" value={result.model} />
          )}
          {result.retrieval_latency_ms != null && (
            <MetricCell label="Retrieval" value={`${result.retrieval_latency_ms}ms`} />
          )}
          {result.synthesis_latency_ms != null && (
            <MetricCell label="Synthesis" value={`${result.synthesis_latency_ms}ms`} />
          )}
          {result.total_latency_ms != null && (
            <MetricCell label="Total" value={`${result.total_latency_ms}ms`} />
          )}
          <MetricCell label="Allowed chunks" value={String(result.allowed_chunks)} />
          <MetricCell label="Denied chunks" value={String(result.denied_chunks)} />
          {result.chunks_used_final != null && result.chunks_available != null && (
            <MetricCell
              label="Chunks used"
              value={`${result.chunks_used_final} of ${result.chunks_available}${result.retry_used ? ' (retry)' : ''}`}
            />
          )}
          {result.llm_calls != null && (
            <MetricCell label="LLM calls" value={String(result.llm_calls)} />
          )}
        </div>
      </div>

      {/* Raw JSON toggle */}
      <div className="rounded-lg border bg-card">
        <button
          onClick={onToggleRawJson}
          className="flex items-center gap-2 w-full px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          {showRawJson ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          View Raw JSON
        </button>
        {showRawJson && (
          <pre className="px-6 pb-4 text-xs overflow-x-auto">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function MetricCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}

function getOutcomeDisplay(outcome: string, isPartial: boolean) {
  if (outcome === 'answered' && !isPartial) {
    return {
      icon: CheckCircle2,
      label: 'Answer',
      className: 'text-status-allow',
      bgClassName: 'bg-status-allow/10',
      body: null,
    };
  }
  if (outcome === 'answered' && isPartial) {
    return {
      icon: AlertTriangle,
      label: 'Partial Answer',
      className: 'text-status-warning',
      bgClassName: 'bg-status-warning/10',
      body: null,
    };
  }
  if (outcome === 'insufficient_context') {
    return {
      icon: AlertTriangle,
      label: 'Insufficient Context',
      className: 'text-status-warning',
      bgClassName: 'bg-status-warning/10',
      body: 'Allowed chunks were found, but they did not support a grounded answer.',
    };
  }
  // no_access or unknown
  return {
    icon: XCircle,
    label: 'No Access',
    className: 'text-status-deny',
    bgClassName: 'bg-status-deny/10',
    body: 'No allowed chunks were available for this principal under current policies.',
  };
}
